import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { saveSignupProgress, getSignupProgress, SignupStep, shouldRedirectToProgress } from '../utils/signupProgress';
import api from '../services/authService';

function UserProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || '';
  const formDataFromState = location.state?.formData;
  
  const [profileData, setProfileData] = useState({
    website: '',
    bio: '',
    gender: 'prefer_not_to_say',
    instagram: '',
    tiktok: '',
    youtube: '',
    facebook: ''
  });
  
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Check for saved progress and restore if needed
  useEffect(() => {
    const redirect = shouldRedirectToProgress('/user-profile');
    if (redirect && redirect.step === SignupStep.SAMPLE_VIDEOS) {
      navigate('/sample-videos', { state: redirect.data });
      return;
    }
    
    // Get username and name from signup form data
    const savedProgress = getSignupProgress();
    const signupFormData = formDataFromState || savedProgress?.data?.formData || {};
    
    if (signupFormData.username) {
      setUsername(signupFormData.username);
    }
    if (signupFormData.name) {
      setName(signupFormData.name);
    }
    
    // Restore profile data from saved progress
    if (savedProgress?.data?.profileData) {
      setProfileData(savedProgress.data.profileData);
    }
    
    // Restore profile picture preview if available
    if (savedProgress?.data?.profilePicturePreview) {
      setProfilePicturePreview(savedProgress.data.profilePicturePreview);
    }
    
    // Save progress when component mounts (only once)
    const progressData = {
      email: emailFromState || savedProgress?.data?.email || '',
      formData: formDataFromState || savedProgress?.data?.formData || {},
      profileData: savedProgress?.data?.profileData || profileData
    };
    
    if (progressData.email) {
      saveSignupProgress(SignupStep.PROFILE, progressData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, emailFromState, formDataFromState]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newProfileData = {
      ...profileData,
      [name]: value
    };
    setProfileData(newProfileData);
    
    // Save progress as user types
    const savedProgress = getSignupProgress();
    saveSignupProgress(SignupStep.PROFILE, {
      email: emailFromState || savedProgress?.data?.email || '',
      formData: formDataFromState || savedProgress?.data?.formData || {},
      profileData: newProfileData,
      profilePicturePreview: profilePicturePreview
    });
  };
  
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setProfilePicture(file);
    
    // Create preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Auto-upload the file
    try {
      setUploadingPhoto(true);
      setError('');
      
      const emailToUse = emailFromState || getSignupProgress()?.data?.email || '';
      
      const formData = new FormData();
      formData.append('profilePicture', file);
      formData.append('email', emailToUse);
      
      const response = await api.post('/users/profile/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Update preview with new URL from server
      if (response.data.fileUrl) {
        setProfilePicturePreview(response.data.fileUrl);
        // Save to progress
        const savedProgress = getSignupProgress();
        saveSignupProgress(SignupStep.PROFILE, {
          email: emailToUse,
          formData: formDataFromState || savedProgress?.data?.formData || {},
          profileData: profileData,
          profilePicturePreview: response.data.fileUrl
        });
      }
      
      setProfilePicture(null);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload profile picture';
      setError(errorMessage);
      // Clear preview on error
      setProfilePicturePreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    setError('');
    
    try {
      const emailToUse = emailFromState || getSignupProgress()?.data?.email || '';
      
      // Update user profile via API
      await api.put('/users/profile', {
        email: emailToUse,
        website: profileData.website,
        bio: profileData.bio,
        gender: profileData.gender,
        affiliateData: {
          instagram: profileData.instagram,
          tiktok: profileData.tiktok,
          youtube: profileData.youtube,
          facebook: profileData.facebook
        }
      });
      
      // Save progress to sample videos step
      const savedProgress = getSignupProgress();
      saveSignupProgress(SignupStep.SAMPLE_VIDEOS, {
        email: emailToUse,
        formData: formDataFromState || savedProgress?.data?.formData || {},
        profileData: profileData
      });
      
      navigate('/sample-videos', {
        state: {
          email: emailToUse,
          formData: formDataFromState || savedProgress?.data?.formData || {},
          profileData: profileData
        }
      });
    } catch (err) {
      console.error('Error saving profile:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save profile. Please try again.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  const displayEmail = emailFromState || getSignupProgress()?.data?.email || '';
  
  const bioLength = profileData.bio?.length || 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src="/icob-bhd.png"
            alt="Boost Health Daily"
            style={{ maxWidth: '140px', height: 'auto' }}
          />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'left' }}>
          Edit profile
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Profile Picture Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Avatar
              src={profilePicturePreview || undefined}
              sx={{
                width: 64,
                height: 64,
                bgcolor: '#e0e0e0',
                fontSize: '2rem'
              }}
            >
              {!profilePicturePreview && (name ? name.charAt(0).toUpperCase() : username ? username.charAt(0).toUpperCase() : 'U')}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {username || 'username'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                {name || 'Full Name'}
              </Typography>
            </Box>
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleProfilePictureChange}
              />
              <label htmlFor="profile-picture-upload">
                <Button
                  component="span"
                  variant="contained"
                  disabled={uploadingPhoto}
                  sx={{
                    backgroundColor: '#0b57d0',
                    textTransform: 'none',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: '#0a4ab3'
                    }
                  }}
                >
                  {uploadingPhoto ? (
                    <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                  ) : (
                    'Change photo'
                  )}
                </Button>
              </label>
            </Box>
          </Box>

          {/* Website Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Website
            </Typography>
            <TextField
              fullWidth
              name="website"
              value={profileData.website}
              onChange={handleChange}
              placeholder="Website"
              variant="outlined"
              sx={{ mb: 1 }}
            />
       
          </Box>

          {/* Bio Section */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Bio
            </Typography>
            <TextField
              fullWidth
              name="bio"
              value={profileData.bio}
              onChange={handleChange}
              placeholder="Bio"
              variant="outlined"
              multiline
              rows={4}
              inputProps={{ maxLength: 150 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#666666',
                fontSize: '0.75rem',
                position: 'absolute',
                right: 16,
                bottom: 8
              }}
            >
              {bioLength}/150
            </Typography>
          </Box>

          {/* Gender Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Gender
            </Typography>
            <FormControl fullWidth>
              <Select
                name="gender"
                value={profileData.gender}
                onChange={handleChange}
                displayEmpty
                sx={{ mb: 1 }}
              >
                <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: '#666666', fontSize: '0.75rem' }}>
              This won't be part of your public profile.
            </Typography>
          </Box>

          {/* Social Media Handles */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              label="instagram handle"
              name="instagram"
              value={profileData.instagram}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="tiktok handle"
              name="tiktok"
              value={profileData.tiktok}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="youtube channel"
              name="youtube"
              value={profileData.youtube}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Facebook page"
              name="facebook"
              value={profileData.facebook}
              onChange={handleChange}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={saving}
            sx={{
              backgroundColor: '#0b57d0',
              borderRadius: 999,
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1.1rem',
              '&:hover': {
                backgroundColor: '#0a4ab3'
              },
              '&:disabled': {
                backgroundColor: '#cccccc'
              }
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default UserProfile;

